import * as React from 'react';
import { FC, useState } from 'react';
import {
    useTranslate,
    useEditContext,
    List,
    Datagrid,
    ListProps,
    FunctionField,
} from 'react-admin';
import { useNavigate } from 'react-router-dom';
import CustomEmpty from './CustomEmpty';
import CustomSelectInput from './CustomSelectInput';
import CustomUserLicenseField from './CustomUserLicenseField';
import ProductFullNameField from '../product/ProductFullNameField';
import WarningIcon from '@mui/icons-material/Warning';
import HtmlTooltip from './HtmlTooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import get from 'lodash/get';

type SeasonKey = 'all' | 'previous' | 'current' | 'future';

interface Props extends ListProps {
    bulkActionButtons?:
        | false
        | React.ReactElement<any, string | React.JSXElementConstructor<any>>
        | undefined;
    sortable?: boolean;
    showExtra?: any;
    hideFields?: any;
    customFilters?: any;
    refreshKey?: string;
    showWarning?: {
        applications: string[];
        checkStatus?: boolean;
        targetPersons?: string[];
    };
}

const CustomUserLicenseByUserList: FC<Props> = ({
    bulkActionButtons,
    sortable = true,
    showExtra = [],
    hideFields = [],
    customFilters = [],
    refreshKey = 'refreshAll',
    showWarning,
    ...props
}) => {
    const translate = useTranslate();
    const navigate = useNavigate();
    const parent = useEditContext();
    const [season, setSeason] = useState<SeasonKey>('all');
    const filterObj = {
        ...(customFilters.includes('user_guid')
            ? { user_guid: parent.record.guid }
            : {}),
        ...(season !== 'all' ? { season } : {}),
    };
    const filter = Object.keys(filterObj).length ? filterObj : undefined;

    const seasonFilter = showExtra.includes('seasonFilter') ? (
        <Box
            sx={{
                pt: 1,
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-start',
            }}
        >
            <ToggleButtonGroup
                size="small"
                exclusive
                value={season}
                onChange={(_e, value) => {
                    if (value !== null) setSeason(value);
                }}
                aria-label={translate('resources.userlicense.seasons.label')}
            >
                <ToggleButton value="all">
                    {translate('resources.userlicense.seasons.all')}
                </ToggleButton>
                <ToggleButton value="previous">
                    {translate('resources.userlicense.seasons.previous')}
                </ToggleButton>
                <ToggleButton value="current">
                    {translate('resources.userlicense.seasons.current')}
                </ToggleButton>
                <ToggleButton value="future">
                    {translate('resources.userlicense.seasons.future')}
                </ToggleButton>
            </ToggleButtonGroup>
        </Box>
    ) : (
        false
    );

    // Helper function to check if product applications match warning applications
    const hasApplicationMismatch = (record: any) => {
        if (!showWarning?.applications?.length) return false;

        const productApplications = get(
            record,
            'product.public.applications',
            []
        );
        if (!productApplications?.length) return true; // No applications in product

        // Check if any of the product applications match the warning applications
        const hasMatch = productApplications.some((app: string) =>
            showWarning.applications.includes(app)
        );

        return !hasMatch; // Return true if no match found (needs warning)
    };

    // Helper function to check if product status is not active (not 0)
    const hasStatusWarning = (record: any) => {
        if (!showWarning?.checkStatus) return false;
        return get(record, 'product.status', 0) !== 0;
    };

    // Helper function to check if product target_persons match required targets
    const hasTargetPersonsWarning = (record: any) => {
        if (!showWarning?.targetPersons?.length) return false;

        const productTargetPersons = get(
            record,
            'product.public.target_persons',
            []
        );
        if (!productTargetPersons?.length) return true; // No target persons in product

        // Check if any of the product target persons match the required target persons
        const hasMatch = productTargetPersons.some((target: string) =>
            showWarning.targetPersons!.includes(target)
        );

        return !hasMatch; // Return true if no match found (needs warning)
    };

    // Helper function to check if product has any warnings
    const hasAnyWarning = (record: any) => {
        return (
            hasApplicationMismatch(record) ||
            hasStatusWarning(record) ||
            hasTargetPersonsWarning(record)
        );
    };

    return (
        <List
            {...props}
            actions={seasonFilter}
            filter={filter}
            filters={
                showExtra.includes('search')
                    ? [
                          <CustomSelectInput
                              url="extralist/subscription/groups"
                              source="group_guid"
                              label={translate(
                                  'resources.userlicense.fields.groups'
                              )}
                              filter="guid"
                              alwaysOn
                          />,
                      ]
                    : undefined
            }
            empty={
                <>
                    {seasonFilter}
                    <CustomEmpty />
                </>
            }
        >
            <Datagrid
                bulkActionButtons={bulkActionButtons}
                rowClick="edit"
                resource="subscription"
            >
                {!hideFields.includes('product') && (
                    <ProductFullNameField
                        source="product"
                        key="product.name"
                        createLink={true}
                        useIcon={false}
                        sortable={sortable}
                    />
                )}
                {showWarning && (
                    <FunctionField
                        label=" "
                        render={record => {
                            if (!hasAnyWarning(record)) return '';

                            return (
                                <HtmlTooltip
                                    title={
                                        <React.Fragment>
                                            <Typography
                                                color="inherit"
                                                fontWeight="bold"
                                                fontSize="14px"
                                                sx={{ mb: 1 }}
                                            >
                                                {translate(
                                                    'resources.product.warnings.title'
                                                )}
                                            </Typography>
                                            <Typography
                                                color="inherit"
                                                fontSize="12px"
                                                sx={{ mb: 2, opacity: 0.8 }}
                                            >
                                                {translate(
                                                    'resources.product.warnings.subtitle'
                                                )}
                                            </Typography>
                                            {hasStatusWarning(record) && (
                                                <Typography
                                                    color="inherit"
                                                    fontSize="12px"
                                                    sx={{
                                                        mb: 1,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            marginRight: '8px',
                                                            color: '#ff9800',
                                                        }}
                                                    >
                                                        •
                                                    </span>
                                                    {translate(
                                                        'resources.product.warnings.status'
                                                    )}
                                                </Typography>
                                            )}

                                            {hasApplicationMismatch(record) && (
                                                <Typography
                                                    color="inherit"
                                                    fontSize="12px"
                                                    sx={{
                                                        mb: 1,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            marginRight: '8px',
                                                            color: '#ff9800',
                                                        }}
                                                    >
                                                        •
                                                    </span>
                                                    {translate(
                                                        'resources.product.warnings.applications'
                                                    )}
                                                </Typography>
                                            )}

                                            {hasTargetPersonsWarning(
                                                record
                                            ) && (
                                                <Typography
                                                    color="inherit"
                                                    fontSize="12px"
                                                    sx={{
                                                        mb: 0,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            marginRight: '8px',
                                                            color: '#ff9800',
                                                        }}
                                                    >
                                                        •
                                                    </span>
                                                    {translate(
                                                        'resources.product.warnings.target_persons'
                                                    )}
                                                </Typography>
                                            )}
                                        </React.Fragment>
                                    }
                                >
                                    <WarningIcon
                                        style={{
                                            fontSize: 'large',
                                            margin: '0px',
                                            color: '#ff9800',
                                            cursor: 'pointer',
                                        }}
                                        onClick={e => {
                                            e.stopPropagation();
                                            navigate(
                                                `/product/${get(record, 'product.guid')}`
                                            );
                                        }}
                                    />
                                </HtmlTooltip>
                            );
                        }}
                    />
                )}
                {!hideFields.includes('licenses') && (
                    <CustomUserLicenseField
                        source="licenses"
                        label={translate(
                            'resources.userlicense.fields.licenses'
                        )}
                        userGuid={parent?.record?.guid}
                        schools={parent?.record?.schools}
                    />
                )}
            </Datagrid>
        </List>
    );
};

export default CustomUserLicenseByUserList;
